import React, { FC, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import toast from "react-hot-toast";
import { ContactFun } from "api/profile/profile";
import SiteHeader from "../SiteHeader";
import Label from "designs/DesignB/components/Label/Label";
import { useData } from "data/data-provider";
import { isUsableUrl } from "utils/isUsableUrl";
import BannerScrollCue from "designs/DesignB/components/BannerScrollCue/BannerScrollCue";

export interface PageContactProps {
  className?: string;
}

/**
 * Design B — Contact page.
 *
 * Top section: a full-screen banner image (same source + sizing as the
 * Home/About hero), with only the SiteHeader overlaid — no marketing copy
 * over the image. We render the SiteHeader inside this page because the
 * router skips the global header for `/contact` (see DesignBRouter).
 *
 * Body: address / phone / email rows alongside the contact form. Earlier
 * versions of this page pulled phone/email/address from `globalJson`,
 * which is hardcoded "N/A" placeholders — that's been replaced with the
 * FMS-managed `companyDetails` so every tenant sees their own data and
 * empty fields hide cleanly.
 *
 * Form posts to `/api/company/contact-us/` with the active company id.
 */
const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const { companyDetails, banner } = useData();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const companyName: string =
    (companyDetails?.company_name || "").trim() ||
    (companyDetails?.company?.name || "").trim() ||
    "";
  const pageTitle = companyName ? `Contact ${companyName}` : "Contact";

  // Full-screen banner matching the Home/About hero: paint the same banner
  // image (desktop image on lg+, mobile image below lg), guarding literal
  // "undefined"/"null" URLs. The contact hero used to be a short band sized to
  // its overlaid text; it now shows the full banner image like the other pages.
  const desktopBannerBg = isUsableUrl(banner?.image_url) ? banner.image_url : "";
  const mobileBannerBgCandidate = banner?.mobile_image_url || banner?.image_url;
  const mobileBannerBg = isUsableUrl(mobileBannerBgCandidate)
    ? mobileBannerBgCandidate
    : "";

  const contacts = useMemo(() => {
    const rows: {
      id: string;
      icon: React.ReactNode;
      title: string;
      detail?: string;
      href?: string;
    }[] = [];

    // Themed icon tile shared across rows. The fill picks up the active
    // theme primary so the contact rows match Design B's gold/navy/etc.
    // palette automatically (vs the old hardcoded #FFCC00 yellow).
    const iconTileClass =
      "flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-2xl bg-primary-200/15 ring-1 ring-primary-200/30";

    if (companyDetails?.company_phone) {
      const phone = String(companyDetails.company_phone).trim();
      rows.push({
        id: "phone",
        title: "Phone",
        detail: phone,
        href: `tel:${phone.replace(/[^+\d]/g, "")}`,
        icon: (
          <span className={iconTileClass}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary-300"
              aria-hidden
            >
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ),
      });
    }

    if (companyDetails?.company_email) {
      const mail = String(companyDetails.company_email).trim();
      rows.push({
        id: "email",
        title: "Email",
        detail: mail,
        href: `mailto:${mail}`,
        icon: (
          <span className={iconTileClass}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary-300"
              aria-hidden
            >
              <path
                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M22 6l-10 7L2 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ),
      });
    }

    if (companyDetails?.address) {
      rows.push({
        id: "address",
        title: "Address",
        detail: String(companyDetails.address).trim(),
        icon: (
          <span className={iconTileClass}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-primary-300"
              aria-hidden
            >
              <path
                d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="12"
                cy="10"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ),
      });
    }

    return rows;
  }, [companyDetails]);

  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const result = await ContactFun({
        company: companyDetails?.company,
        name: trimmedName,
        email: trimmedEmail,
        message: trimmedMessage,
      });
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success("Message sent — we'll be in touch shortly.");
        setName("");
        setEmail("");
        setMessage("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {/* ----- Hero banner — full-screen image, matching Home/About. The old
          written-line overlay (eyebrow pill + "Talk to {company}" heading +
          subtitle) was removed; the banner now shows the full image like the
          other pages, with only the site header on top. ----- */}
      <div
        className={`hidden lg:flex justify-between flex-col bg-cover bg-center w-full h-screen relative ${className}`}
        style={{
          backgroundImage: desktopBannerBg ? `url(${desktopBannerBg})` : undefined,
          backgroundSize: banner?.position ?? "cover",
          backgroundPosition: banner?.image_alignment ?? "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <SiteHeader />
        <BannerScrollCue />
      </div>
      <div
        className="flex lg:hidden justify-between flex-col bg-cover bg-center w-full h-[100dvh] relative"
        style={{
          backgroundImage: mobileBannerBg ? `url(${mobileBannerBg})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: banner?.image_alignment ?? "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <SiteHeader />
        <BannerScrollCue />
      </div>

      {/* ----- Body ----- */}
      <div id="section" className="bg-bg-color">
        <div className="container mx-auto py-14 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact rows */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-text-color">
                Reach Out
              </h2>
              <p className="mt-3 text-base sm:text-lg text-secondary-text-color leading-relaxed max-w-md">
                Pick the channel that suits you. We answer every message — usually
                within one business day.
              </p>

              {contacts.length > 0 && (
                <ul className="mt-8 space-y-5">
                  {contacts.map((row) => (
                    <li key={row.id} className="flex items-start gap-4">
                      {row.icon}
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-secondary-text-color">
                          {row.title}
                        </p>
                        {row.href ? (
                          <a
                            href={row.href}
                            className="mt-1 block text-base sm:text-lg font-semibold text-text-color hover:text-primary-300 break-words"
                          >
                            {row.detail}
                          </a>
                        ) : (
                          <p className="mt-1 text-base sm:text-lg font-semibold text-text-color whitespace-pre-line break-words">
                            {row.detail}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Form */}
            <div className="rounded-3xl bg-card-color ring-1 ring-secondary-100 shadow-xl shadow-text-color/5 p-6 sm:p-8 lg:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-text-color">
                Send a Message
              </h2>
              <p className="mt-2 text-sm text-secondary-text-color">
                We'll never share your details.
              </p>
              <form
                className="mt-6 grid grid-cols-1 gap-5"
                onSubmit={sendEmail}
                noValidate
              >
                <label className="block">
                  <Label>Full name</Label>
                  <Input
                    placeholder="Your name"
                    type="text"
                    className="mt-1"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </label>
                <label className="block">
                  <Label>Email address</Label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    className="mt-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label className="block">
                  <Label>Message</Label>
                  <Textarea
                    className="mt-1"
                    placeholder="How can we help?"
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </label>
                <ButtonPrimary
                  type="submit"
                  className="min-w-[200px] justify-self-start"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <span className="spinner-border animate-spin inline-block w-5 h-5 border-4 rounded-full border-t-transparent border-gray-300" />
                    </span>
                  ) : (
                    "Send Message"
                  )}
                </ButtonPrimary>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PageContact;
